// src/pages/NotificationsFeed.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import API from "../api";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import { useSelector } from "react-redux";
import { selectUser } from "../store/authSlice";
import LeadDetailsModal from "../pages/Lead/LeadDetailsModal";
import "./css/notifications.css";

const socket = io(process.env.REACT_APP_API_BASE); // e.g., http://localhost:5000

function formatDateOnly(raw) {
  if (!raw) return "";
  if (typeof raw === "string" && raw.includes(" ")) return raw.split(" ")[0]; // "YYYY-MM-DD"
  try {
    const d = new Date(raw);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
  } catch {}
  return String(raw);
}

export default function NotificationsFeed() {
  const user = useSelector(selectUser);
  const ass = useMemo(() => (user?.ass ?? 2), [user]); // default 2 if null/undefined

  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  // LeadDetailsModal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalLead, setModalLead] = useState(null);

  // track newest timestamp for incremental polling
  const lastSeenRef = useRef(null); // ISO string

  const mergeNew = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setItems((prev) => {
      const ids = new Set(prev.map((x) => x.id));
      const fresh = rows.filter((r) => !ids.has(r.id));
      return [...fresh, ...prev];
    });
    const newest = rows
      .map((r) => new Date(r.created_at).getTime())
      .filter(Number.isFinite);
    if (newest.length) {
      lastSeenRef.current = new Date(Math.max(...newest)).toISOString();
    }
  };

  // initial load (no since)
  const initialLoad = async () => {
    try {
      const resp = await API.get("/notifications", { params: { ass } });
      const data = Array.isArray(resp?.data) ? resp.data : [];
      setItems(data);
      const newest = data
        .map((r) => new Date(r.created_at).getTime())
        .filter(Number.isFinite);
      if (newest.length)
        lastSeenRef.current = new Date(Math.max(...newest)).toISOString();
    } catch (err) {
      console.error("Failed to load notifications:", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
      toast.error("Failed to load notifications");
    }
  };

  // incremental refresh every 20s
  const refreshSince = async () => {
    try {
      const params = { ass };
      if (lastSeenRef.current) params.since = lastSeenRef.current;
      const resp = await API.get("/notifications", { params });
      const data = Array.isArray(resp?.data) ? resp.data : [];
      mergeNew(data);
    } catch (err) {
      console.error("Failed to refresh notifications:", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });
    }
  };

  // request desktop notification permission once on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // load on mount / ass change
  useEffect(() => {
    setItems([]);
    lastSeenRef.current = null;
    initialLoad();
  }, [ass]);

  // polling
  useEffect(() => {
    const t = setInterval(refreshSince, 20_000);
    return () => clearInterval(t);
  }, [ass]);

  // sockets
  useEffect(() => {
    const onNew = (rows) => {
      const filtered = rows.filter((r) => (r.ass ?? ass) === ass);
      if (!filtered.length) return;
      mergeNew(filtered);

      filtered.forEach((n) =>
        toast.info(`${n.name} (${n.bname}) — ${n.comment}`, {
          position: "top-right",
          autoClose: 5000,
        })
      );

      if (Notification.permission === "granted") {
        filtered.forEach((n) =>
          new Notification(`${n.name} (${n.bname})`, {
            body: `${n.comment} • Agent: ${n.agent} • Time: ${n.calldate} ${n.calltime}`,
          })
        );
      }
    };

    const onRemoved = ({ id }) =>
      setItems((prev) => prev.filter((n) => n.id !== id));
    const onCleared = () => setItems([]);

    socket.on("notifications:new", onNew);
    socket.on("notifications:removed", onRemoved);
    socket.on("notifications:cleared", onCleared);

    return () => {
      socket.off("notifications:new", onNew);
      socket.off("notifications:removed", onRemoved);
      socket.off("notifications:cleared", onCleared);
    };
  }, [ass]);

  // delete one (used by ✕)
  const removeOne = async (id) => {
    if (!id) return;
    try {
      setBusy(true);
      setItems((prev) => prev.filter((n) => n.id !== id)); // optimistic
      await API.delete(`/notifications/${id}`);
      toast.success("Notification removed");
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete notification");
      // optional: reload to be safe
      initialLoad();
    } finally {
      setBusy(false);
    }
  };

  // clear all
  const clearAll = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    try {
      setBusy(true);
      setItems([]); // optimistic
      await API.delete("/notifications");
      lastSeenRef.current = null;
      toast.success("All notifications cleared");
    } catch (e) {
      console.error(e);
      toast.error("Failed to clear notifications");
      initialLoad();
    } finally {
      setBusy(false);
    }
  };

  // CLICK → open lead details AND delete that notification
  const openFromNotification = async (notif) => {
    if (!notif) return;

    // 1) Optimistically remove the notification row
    if (notif.id) setItems((prev) => prev.filter((n) => n.id !== notif.id));
    // fire-and-forget delete; if it fails, polling/socket will sync
    if (notif.id) API.delete(`/notifications/${notif.id}`).catch(() => {});

    // 2) Open modal and fetch lead by lead_id
    if (!notif.lead_id) return; // nothing to open if no lead id
    setModalOpen(true);
    setModalLoading(true);
    setModalError("");
    setModalLead(null);
    try {
      const resp = await API.get(`/auth/lead/${notif.lead_id}`);
      const lead = resp?.data?.lead || resp?.data;
      setModalLead(lead || null);
    } catch (e) {
      console.error(e);
      setModalError("Could not load lead details.");
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <div className="nf-wrap">
        <div className="nf-header">
          <h3 className="nf-title">Notifications</h3>
          <div className="nf-actions">
            <button
              type="button"
              className="nf-btn nf-btn-secondary"
              onClick={initialLoad}
              disabled={busy}
              title="Refresh"
            >
              Refresh
            </button>
            <button
              type="button"
              className="nf-btn nf-btn-danger"
              onClick={clearAll}
              disabled={busy || items.length === 0}
              title="Clear all"
            >
              Clear all
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="nf-empty">No notifications</div>
        ) : (
          <ul className="nf-list">
            {items.map((n) => (
              <li
                className="nf-item"
                key={n.id}
                onClick={() => openFromNotification(n)}
              >
                <div className="nf-item-main">
                  <div className="nf-line">
                    <strong className="nf-name">{n.name}</strong>
                    {n.bname ? <span className="nf-biz">({n.bname})</span> : null}
                  </div>
                  {n.comment ? (
                    <div className="nf-comment">{n.comment}</div>
                  ) : null}
                  <div className="nf-meta">
                    <span>
                      {formatDateOnly(n.calldate)} {n.calltime?.slice(0, 5)}
                    </span>
                    <span>Agent: {n.agent || "—"}</span>
                    <span>AID: {n.aid ?? "—"}</span>
                  </div>
                </div>

                <div className="nf-item-actions">
                  <button
                    className="nf-icon-btn"
                    aria-label="Delete"
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation(); // don't open modal when pressing ✕
                      removeOne(n.id);
                    }}
                    disabled={busy}
                  >
                    ✕
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Lead details modal */}
      <LeadDetailsModal
        open={modalOpen}
        loading={modalLoading}
        error={modalError}
        lead={modalLead}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
